"use client";

import { getAuthHeaders } from "@/infrastructure/http/authHeaders";
import { createIdempotencyKey } from "@/infrastructure/http/request-id";
import { httpClient } from "@/infrastructure/http/HttpClient";

const API_BASE_URL =
  process.env.NEXT_PUBLIC_CUSTDEV_API_URL || "http://localhost:8200";

export interface ProjectResponse {
  id: string;
  name: string;
  description: string | null;
  status: string;
  records_count: number;
  total_reports_count?: number;
  latest_reports_count?: number;
  /** Pending + Active interview links that reserve tokens. */
  active_interview_rooms_count?: number;
  created_at: string;
  research_goals?: string | null;
  interview_guide?: string | null;
  owner_email?: string | null;
  file_search_index?: ProjectFileSearchIndexResponse | null;
}

export interface ProjectFileSearchIndexResponse {
  status: "not_initialized" | "indexing" | "indexed" | "failed";
  indexed_documents_count?: number | null;
  pending_operations_count?: number | null;
  indexed_documents_progress_percent?: number | null;
  last_indexed_at: string | null;
  updated_at: string | null;
  error: string | null;
}

export interface ProjectRecordResponse {
  id: string;
  name: string;
  file_name: string;
  file_extension: string;
  status: string;
  is_public: boolean;
  is_archived: boolean;
  is_deleted?: boolean;
  size_mb: number;
  duration_sec: number;
  creation_date: string;
  modified_date: string;
  has_transcript: boolean;
  has_reports: boolean;
}

export type ProjectsApiAuthErrorCode =
  | "AUTH_UNVERIFIED"
  | "AUTH_EXPIRED"
  | "AUTH_INVALID";

export class ProjectsApiAuthError extends Error {
  code: ProjectsApiAuthErrorCode;
  status: number;

  constructor(code: ProjectsApiAuthErrorCode, status: number, detail: string) {
    super(`${code}: ${detail}`);
    this.name = "ProjectsApiAuthError";
    this.code = code;
    this.status = status;
  }
}

function normalizeAuthCode(detail: unknown, status: number): ProjectsApiAuthErrorCode {
  if (status === 401) {
    return "AUTH_EXPIRED";
  }

  const message = typeof detail === "string" ? detail.toLowerCase() : "";
  const hasCode =
    detail &&
    typeof detail === "object" &&
    "code" in (detail as { code?: unknown }) &&
    typeof (detail as { code?: unknown }).code === "string"
      ? String((detail as { code?: unknown }).code!).toLowerCase()
      : "";

  if (hasCode.includes("unverified") || message.includes("unverified")) {
    return "AUTH_UNVERIFIED";
  }

  if (hasCode.includes("expired") || hasCode.includes("token_expired")) {
    return "AUTH_EXPIRED";
  }

  return "AUTH_INVALID";
}

async function withAuthHeaders(): Promise<Record<string, string>> {
  return getAuthHeaders();
}

function parseErrorDetail(detail: unknown): string {
  if (typeof detail === "string") {
    return detail;
  }

  if (
    detail &&
    typeof detail === "object" &&
    "detail" in (detail as { detail?: unknown }) &&
    typeof (detail as { detail?: unknown }).detail === "string"
  ) {
    return String((detail as { detail?: unknown }).detail);
  }

  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        if (typeof item === "string") {
          return item;
        }

        if (
          item &&
          typeof item === "object" &&
          "msg" in item &&
          typeof (item as { msg?: unknown }).msg === "string"
        ) {
          return (item as { msg: string }).msg;
        }

        return "";
      })
      .filter(Boolean)
      .join(", ");
  }

  return "";
}

function isUuidValidationError(detail: unknown): boolean {
  if (!Array.isArray(detail)) {
    return false;
  }

  return detail.some(
    (item) =>
      item &&
      typeof item === "object" &&
      "type" in item &&
      (item as { type?: string }).type === "uuid_parsing"
  );
}

async function readErrorDetail(response: Response): Promise<unknown> {
  const text = await response.text().catch(() => "");
  if (!text) {
    return undefined;
  }

  try {
    return JSON.parse(text) as { detail?: unknown };
  } catch {
    return text;
  }
}

async function requestJson<T>(
  url: string,
  context: string,
  options: RequestInit = {},
  opts: {
    requiresAuth?: boolean;
    idempotencyKey?: string;
    uuidErrorMessage?: string;
  } = {}
): Promise<T> {
  const headers = new Headers(options.headers);

  if (opts.requiresAuth) {
    const authHeaders = await withAuthHeaders();
    Object.entries(authHeaders).forEach(([key, value]) => {
      headers.set(key, value);
    });
  }

  const response = await httpClient.request(
    url,
    {
      ...options,
      headers,
    },
    {
      idempotencyKey: opts.idempotencyKey,
    }
  );

  if (!response.ok) {
    const detailPayload = await readErrorDetail(response);
    const detail =
      detailPayload && typeof detailPayload === "object" && "detail" in detailPayload
        ? (detailPayload as { detail?: unknown }).detail
        : detailPayload;

    if (response.status === 401 || response.status === 403) {
      throw new ProjectsApiAuthError(
        normalizeAuthCode(detailPayload, response.status),
        response.status,
        parseErrorDetail(detail) || response.statusText
      );
    }

    if (isUuidValidationError(detail) && opts.uuidErrorMessage) {
      throw new Error(opts.uuidErrorMessage);
    }

    const parsedDetail = parseErrorDetail(detail);
    if (parsedDetail) {
      throw new Error(`${context}: ${parsedDetail}`);
    }

    if (typeof detail === "string" && detail.trim()) {
      throw new Error(`${context}: ${detail}`);
    }

    throw new Error(`${context}: ${response.statusText}`);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

interface GetProjectsOptions {
  includeFileSearchIndex?: boolean;
}

function withIncludeQuery(baseUrl: string, includeFileSearchIndex?: boolean): string {
  if (!includeFileSearchIndex) return baseUrl;
  const separator = baseUrl.includes("?") ? "&" : "?";
  return `${baseUrl}${separator}include=file_search_index`;
}

export async function getProjects(
  options: GetProjectsOptions = {}
): Promise<ProjectResponse[]> {
  const url = withIncludeQuery(
    `${API_BASE_URL}/projects/`,
    options.includeFileSearchIndex
  );
  return requestJson<ProjectResponse[]>(url, "Failed to fetch projects", {
    method: "GET",
  }, {
    requiresAuth: true,
  });
}

export async function getAdminProjects(
  options: GetProjectsOptions = {}
): Promise<ProjectResponse[]> {
  const url = withIncludeQuery(
    `${API_BASE_URL}/projects/admin`,
    options.includeFileSearchIndex
  );
  return requestJson<ProjectResponse[]>(
    url,
    "Failed to fetch admin projects",
    { method: "GET" },
    { requiresAuth: true }
  );
}

export async function getProjectDetails(
  projectId: string
): Promise<ProjectResponse> {
  const url = `${API_BASE_URL}/projects/${projectId}`;
  return requestJson<ProjectResponse>(url, "Failed to fetch project", {
    method: "GET",
  }, {
    requiresAuth: true,
    uuidErrorMessage: "Invalid project ID format. Please use a valid project ID.",
  });
}

export async function getPublicProjectDetails(
  projectId: string
): Promise<ProjectResponse> {
  const url = `${API_BASE_URL}/projects/public/${projectId}`;
  return requestJson<ProjectResponse>(url, "Failed to fetch public project", {
    method: "GET",
  }, {
    uuidErrorMessage: "Invalid project ID format. Please use a valid project ID.",
  });
}

export async function getProjectRecords(
  projectId: string
): Promise<ProjectRecordResponse[]> {
  const url = `${API_BASE_URL}/projects/${projectId}/records`;
  return requestJson<ProjectRecordResponse[]>(
    url,
    "Failed to fetch project records",
    {
      method: "GET",
    },
    {
      requiresAuth: true,
      uuidErrorMessage: "Invalid project ID format. Please use a valid project ID.",
    }
  );
}

export async function createProject(
  name: string,
  description?: string,
  researchGoals?: string,
  interviewGuide?: string
): Promise<ProjectResponse> {
  const url = `${API_BASE_URL}/projects/create`;
  return requestJson<ProjectResponse>(
    url,
    "Failed to create project",
    {
      method: "POST",
      body: JSON.stringify({
        name,
        description: description || null,
        research_goals: researchGoals || null,
        interview_guide: interviewGuide || null,
      }),
    },
    {
      requiresAuth: true,
      idempotencyKey: createIdempotencyKey("project-create", name),
    }
  );
}

export async function updateProject(
  projectId: string,
  updates: {
    name?: string;
    description?: string;
    status?: string;
    research_goals?: string;
    interview_guide?: string;
  }
): Promise<ProjectResponse> {
  const url = `${API_BASE_URL}/projects/${projectId}`;
  return requestJson<ProjectResponse>(
    url,
    "Failed to update project",
    {
      method: "PUT",
      body: JSON.stringify(updates),
    },
    {
      requiresAuth: true,
      idempotencyKey: createIdempotencyKey("project-update", projectId),
    }
  );
}

export async function deleteProject(projectId: string): Promise<void> {
  const url = `${API_BASE_URL}/projects/${projectId}`;

  try {
    await requestJson<void>(
      url,
      "Unable to delete project",
      {
        method: "DELETE",
      },
      {
        requiresAuth: true,
        idempotencyKey: createIdempotencyKey("project-delete", projectId),
      }
    );
  } catch {
    throw new Error("Unable to delete project. Please try again.");
  }
}
